import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';

// Data-driven single-elimination bracket (Liquipedia composable widget).
//
// The backend does NOT populate the explicit topology fields (`tournament.rounds`
// is empty and `match.round` is null for real tournaments), so the bracket is
// RECONSTRUCTED from data that IS present: each match's stage is parsed from its
// name/slug ("Quarterfinals Match 4", "Grand Finals"), scores are counted from
// round victors, and the winner is `match.victor`. When the stage can't be
// inferred for at least two rounds (e.g. a round-robin/group stage) the whole
// widget hides so the flat MatchList / standings cover it instead.
//
// Args:
//   @matches - tournament matches (teams, victor, rounds, name/slug, logo)
//   @victor  - the tournament winner (Team) for the champion column, optional

// Stage detection. Order matters: compound stages are tested before "final"
// so "Quarterfinals"/"Semifinals" don't match the bare `final` rule. Higher
// rank = earlier round (shown first).
function stageInfo(name, slug) {
  const s = `${name ?? ''} ${slug ?? ''}`.toLowerCase();
  if (/round of 64|\bro64\b/.test(s)) return { rank: 7, label: 'Round of 64' };
  if (/round of 32|\bro32\b/.test(s)) return { rank: 6, label: 'Round of 32' };
  if (/round of 16|\bro16\b/.test(s)) return { rank: 5, label: 'Round of 16' };
  if (/quarter[-\s]?final/.test(s)) return { rank: 4, label: 'Quarterfinals' };
  if (/semi[-\s]?final/.test(s)) return { rank: 3, label: 'Semifinals' };
  if (/third[-\s]?place|3rd[-\s]?place|bronze/.test(s))
    return { rank: 2, label: 'Third Place' };
  if (/grand[-\s]?final/.test(s)) return { rank: 1, label: 'Grand Finals' };
  if (/\bfinals?\b/.test(s)) return { rank: 1, label: 'Finals' };
  return null;
}

// Pull the trailing "Match N" ordinal so matches sort consistently in a round.
function matchOrdinal(match) {
  const m = `${match.slug ?? ''} ${match.name ?? ''}`.match(/match[-\s]?(\d+)/i);
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
}

function scoreFor(match) {
  const [a, b] = match.teams;
  let sa = 0;
  let sb = 0;
  for (const round of match.rounds ?? []) {
    const v = round?.victor?.slug;
    if (v === a.slug) sa += 1;
    else if (v === b.slug) sb += 1;
  }
  return { sa, sb };
}

export default class TournamentBracket extends Component {
  get rounds() {
    const candidates = (this.args.matches ?? []).filter(
      (m) => m && Array.isArray(m.teams) && m.teams.length >= 2,
    );

    const byRank = new Map();
    for (const match of candidates) {
      const stage = stageInfo(match.name ?? match.title, match.slug);
      if (!stage) continue;
      if (!byRank.has(stage.rank)) {
        byRank.set(stage.rank, { rank: stage.rank, label: stage.label, matches: [] });
      }
      const { sa, sb } = scoreFor(match);
      const [a, b] = match.teams;
      const winner = match.victor?.slug;
      byRank.get(stage.rank).matches.push({
        slug: match.slug,
        teamA: { ...a, score: sa, won: winner ? winner === a.slug : sa > sb },
        teamB: { ...b, score: sb, won: winner ? winner === b.slug : sb > sa },
      });
    }

    // Need at least two distinct rounds to read as a bracket.
    if (byRank.size < 2) return [];

    const rounds = [...byRank.values()].sort((x, y) => y.rank - x.rank);
    for (const round of rounds) {
      round.matches.sort((x, y) => matchOrdinal(x) - matchOrdinal(y));
    }
    return rounds;
  }

  <template>
    {{#if this.rounds.length}}
      <div class='widget-item'>
        <div class='section-title-wrap violet small-space'>
          <h2 class='section-title medium'>Tournament Bracket</h2>
          <div class='section-title-separator'></div>
        </div>

        <div class='esports-bracket-scroll'>
          <div class='esports-bracket'>
            {{#each this.rounds as |round|}}
              <div class='ebr-round'>
                <p class='ebr-round-label'>{{round.label}}</p>
                <div class='ebr-round-matches'>
                  {{#each round.matches as |match|}}
                    <LinkTo
                      @route='esports.matches.match'
                      @model={{match.slug}}
                      class='ebr-match'
                    >
                      <div class='ebr-side {{if match.teamA.won "is-winner"}}'>
                        <img class='ebr-logo' src={{match.teamA.logo.url}} alt='' loading='lazy' />
                        <span class='ebr-team'>{{match.teamA.name}}</span>
                        <span class='ebr-score'>{{match.teamA.score}}</span>
                      </div>
                      <div class='ebr-side {{if match.teamB.won "is-winner"}}'>
                        <img class='ebr-logo' src={{match.teamB.logo.url}} alt='' loading='lazy' />
                        <span class='ebr-team'>{{match.teamB.name}}</span>
                        <span class='ebr-score'>{{match.teamB.score}}</span>
                      </div>
                    </LinkTo>
                  {{/each}}
                </div>
              </div>
            {{/each}}

            {{#if @victor}}
              <div class='ebr-round ebr-round--champion'>
                <p class='ebr-round-label'>Champion</p>
                <div class='ebr-round-matches'>
                  <div class='ebr-champion'>
                    <img class='ebr-cup' src='/img/icons/cup-award-icon.png' alt='' />
                    {{#if @victor.logo.url}}
                      <img class='ebr-logo' src={{@victor.logo.url}} alt='' loading='lazy' />
                    {{/if}}
                    <span class='ebr-team'>{{@victor.name}}</span>
                  </div>
                </div>
              </div>
            {{/if}}
          </div>
        </div>
      </div>
    {{/if}}
  </template>
}
